import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaLeaf,
  FaCalendarAlt,
  FaClock,
  FaDollarSign,
  FaTractor,
  FaWalking,
  FaDog,
  FaHome,
  FaRecycle,
  FaArrowLeft,
  FaExchangeAlt,
  FaDoorOpen,
  FaCar,
  FaSchool,
  FaCheckCircle,
} from "react-icons/fa";

import { API_BASE_URL } from "../config";

const placeholderImage = "/images/Grass.jpg";

const formatDuration = (hours) => {
  if (!hours) return "N/A";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  const hourText = h > 0 ? `${h} hr${h > 1 ? "s" : ""}` : "";
  const minText = m > 0 ? `${m} min${m > 1 ? "s" : ""}` : "";
  return `${hourText}${hourText && minText ? " " : ""}${minText}` || "N/A";
};

function SwapDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
const fromMyListings = location.state?.fromMyListings === true;
const fromInbox = location.state?.fromInbox === true;


  const user = JSON.parse(localStorage.getItem("user")) || null;

  const [swap, setSwap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [mySwapListings, setMySwapListings] = useState([]);



 // -------------------------------
// FETCH LISTING
// -------------------------------
useEffect(() => {
  const fetchSwap = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/swap-listings/${id}`);
      if (!res.ok) throw new Error("HTTP " + res.status);

      const data = await res.json();

      // 🔥 BACKEND RETURNS { success, listing, owner }
      const listing = data.listing;

      if (!listing) throw new Error("No listing returned");

      setSwap({
        ...listing,

        // normalise booleans (tinyint 0/1)
        streetParking: listing.streetParking === 1,
        schoolZone: listing.schoolZone === 1,
        hasDogs: listing.hasDogs === 1,
        greenBin: listing.greenBin === 1,

        frontLawn: listing.frontLawn === 1,
        rearLawn: listing.rearLawn === 1,
        natureStrip: listing.natureStrip === 1,
        acreage: listing.acreage == 1 || listing.acreage === true,

        disclaimerAccepted: listing.disclaimerAccepted === 1,
      });
    } catch (err) {
      console.error("❌ Failed to fetch swap:", err);
      setSwap(null);
    } finally {
      setLoading(false);
    }
  };

  fetchSwap();
}, [id]);


  // -------------------------------
  // FETCH USER’S SWAP LISTINGS
  // -------------------------------
  useEffect(() => {
    if (!user?.id) return;

    const fetchMyListings = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/swap-listings/user/${user.id}`
        );
        const data = await res.json();
        setMySwapListings(data.listings || []);
      } catch (err) {
        console.error("❌ Failed to load user swap listings:", err);
      }
    };

    fetchMyListings();
}, [user]);

  const getImageSrc = (image) => {
    if (!image) return placeholderImage;
    if (image.startsWith("http") || image.startsWith("/images/")) return image;
    return `/images/${image.split("/").pop()}`;
  };

  const getGoogleMapsLink = (suburb, postcode, state) => {
  const location = `${suburb || ""} ${postcode || ""} ${state || ""} Australia`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
};

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-AU", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // -------------------------------
// REQUEST SWAP LOGIC
// -------------------------------

const handleRequestSwap = () => {
  if (!user) {
    setMessage("❌ You must be logged in to request a swap.");
    setTimeout(() => navigate("/login"), 1200);
    return;
  }

  navigate(`/mowing/request-swap/${swap.id}`);
};

{message && (
  <div
    style={{
      margin: "15px 0",
      color: message.includes("❌") ? "#d32f2f" : "#2e7d32",
      fontWeight: "bold",
    }}
  >
    {message}
  </div>
)}


  // -------------------------------
  // LOADING CHECK
  // -------------------------------
  if (loading)
    return <p style={{ textAlign: "center", marginTop: "2rem" }}>Loading...</p>;

  if (!swap)
    return (
      <p style={{ textAlign: "center", marginTop: "2rem" }}>
        Swap not found.
      </p>
    );

  // -------------------------------
  // STATUS BADGE + DISABLE LOGIC
  // -------------------------------
  const renderStatusBadge = () => {
    const status = swap.swap_status;

    if (status === "locked")
      return (
        <div style={badgeStyle("#fbc02d", "#000")}>🔒 Swap In Progress</div>
      );

    if (status === "handover")
      return (
        <div style={badgeStyle("#ff7043", "#fff")}>🔄 Handover In Progress</div>
      );

    if (status === "completed")
      return (
        <div style={badgeStyle("#9e9e9e", "#fff")}>✅ Completed</div>
      );

    return null;
  };

  const badgeStyle = (bg, color) => ({
    position: "absolute",
    top: "20px",
    right: "20px",
    background: bg,
    color: color,
    padding: "10px 15px",
    borderRadius: "8px",
    fontWeight: "bold",
    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
    zIndex: 5,
  });
const isOwner = user?.id === swap.userId;

const disableSwap =
  isOwner ||
  swap.swap_status === "locked" ||
  swap.swap_status === "handover" ||
  swap.swap_status === "completed";


  // -------------------------------
  // RENDER UI
  // -------------------------------
  return (
    <div
      style={{
        maxWidth: "950px",
        margin: "2rem auto",
        backgroundColor: "#fff",
        borderRadius: "16px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
        overflow: "hidden",
      }}
    >
      {/* Hero Image */}
      <div style={{ position: "relative" }}>
        {renderStatusBadge()}

        <img
          src={getImageSrc(swap.image)}
          alt="Swap Listing"
          style={{ width: "100%", height: "380px", objectFit: "cover" }}
        />
        <button
          onClick={() => navigate("/swaps")}
          style={{
            position: "absolute",
            top: "15px",
            left: "15px",
            background: "rgba(255,255,255,0.9)",
            border: "none",
            borderRadius: "50%",
            padding: "10px",
            cursor: "pointer",
          }}
        >
          <FaArrowLeft />
        </button>
      </div>

      <div style={{ padding: "25px" }}>
        <h1 style={{ color: "#2e7d32", marginBottom: "5px" }}>
          Swap Request – {swap.listingId}
        </h1>

        {message && (
          <div
            style={{
              margin: "15px 0",
              color: message.includes("❌") ? "#d32f2f" : "#2e7d32",
              fontWeight: "bold",
            }}
          >
            {message}
          </div>
        )}

<Section title="📍 Swap Locations" color="#8e24aa">

  <a
    href={getGoogleMapsLink(
      swap.swap_from_suburb,
      swap.swap_from_postcode,
      swap.swap_from_state
    )}
    target="_blank"
    rel="noopener noreferrer"
    style={{ textDecoration: "none", color: "inherit" }}
  >
    <Info
      icon={<FaMapMarkerAlt />}
      label="From Suburb"
      value={
        swap.swap_from_suburb
          ? `${swap.swap_from_suburb}${swap.swap_from_postcode ? `, ${swap.swap_from_postcode}` : ""}${swap.swap_from_state ? ` (${swap.swap_from_state})` : ""}`
          : "N/A"
      }
      isText
    />
  </a>

  <a
    href={getGoogleMapsLink(
      swap.swap_to_suburb,
      swap.swap_to_postcode,
      swap.swap_to_state
    )}
    target="_blank"
    rel="noopener noreferrer"
    style={{ textDecoration: "none", color: "inherit" }}
  >
    <Info
      icon={<FaMapMarkerAlt />}
      label="To Suburb"
      value={
        swap.swap_to_suburb
          ? `${swap.swap_to_suburb}${swap.swap_to_postcode ? `, ${swap.swap_to_postcode}` : ""}${swap.swap_to_state ? ` (${swap.swap_to_state})` : ""}`
          : "N/A"
      }
      isText
    />
  </a>

</Section>

        {/* ORIGINAL SECTIONS BELOW */}
        <Section title="🧍 Client Overview" color="#7b1fa2">
          <Info icon={<FaDoorOpen />} label="Access Type" value={swap.accessType} isText />
          <Info icon={<FaCar />} label="Street Parking" value={swap.streetParking} />
          <Info icon={<FaSchool />} label="School Zone" value={swap.schoolZone} />
          <Info icon={<FaDog />} label="Has Dogs" value={swap.hasDogs} />
          <Info icon={<FaRecycle />} label="Green Bin" value={swap.greenBin} />
        </Section>

        <Section title="🧰 Service Summary" color="#4caf50">
          <Info icon={<FaClock />} label="Time per Job" value={formatDuration(Number(swap.time))} isText />
          <Info icon={<FaCalendarAlt />} label="Growing Season Frequency" value={swap.frequency} isText />
          <Info icon={swap.rideOnOrPush === "Ride-on" ? <FaTractor /> : <FaWalking />} label="Mower Type" value={swap.rideOnOrPush} isText />
          <Info icon={<FaLeaf />} label="Grass Handling" value={swap.bagMulch} isText />
        </Section>

        <Section title="🌿 Areas Serviced" color="#43a047">
          <Info icon={<FaHome />} label="Front Lawn" value={swap.frontLawn} />
          <Info icon={<FaLeaf />} label="Rear Lawn" value={swap.rearLawn} />
          <Info icon={<FaMapMarkerAlt />} label="Nature Strip" value={swap.natureStrip} />
          <Info icon={<FaLeaf />} label="Acreage Property" value={swap.acreage} />
        </Section>

        <Section title="💰 Client Value" color="#ff9800">
          <Info icon={<FaDollarSign />} label="Cut Value" value={swap.cutValue ? `$${swap.cutValue}` : "N/A"} isText />
        </Section>

        <Section title="✅ Verification" color="#388e3c">
          <Info icon={<FaCheckCircle />} label="Listed On" value={formatDate(swap.createdAt)} isText />
        <Info
  icon={<FaCheckCircle />}
  label="Verification"
  value="Seller confirmed this listing is genuine."
  isText
/>

        </Section>

        {/* REQUEST BUTTON */}
        {!fromMyListings && !fromInbox && (

  <div style={{ marginTop: "30px" }}>
    <button
      onClick={!disableSwap ? handleRequestSwap : null}
      disabled={disableSwap}
      style={{
        ...buttonStyle("#4caf50"),
        opacity: disableSwap ? 0.5 : 1,
        cursor: disableSwap ? "not-allowed" : "pointer",
      }}
    >
    <FaExchangeAlt style={{ marginRight: "6px" }} /> Request Swap

{isOwner && "You cannot choose your own listing"}

{!isOwner && swap.swap_status === "locked" && "Swap In Progress"}

{!isOwner && swap.swap_status === "handover" && "Handover In Progress"}

{!isOwner && swap.swap_status === "completed" && "Swap Completed"}

{!isOwner && swap.swap_status === "active" && "Request Swap"}

    </button>
  </div>
)}

      </div>
    </div>
  );
}

const Section = ({ title, color, children }) => (
  <section style={{ marginTop: "25px" }}>
    <h2 style={{ borderBottom: `2px solid ${color}`, paddingBottom: "5px" }}>
      {title}
    </h2>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "15px",
        marginTop: "10px",
      }}
    >
      {children}
    </div>
  </section>
);

const Info = ({ icon, label, value, isText = false }) => {
  let displayValue = value;
  if (typeof value === "boolean") displayValue = value ? "Yes" : "No";
  if (value === 1 || value === 0) displayValue = value === 1 ? "Yes" : "No";

  return (
    <div
      style={{
        display: "flex",
        cursor: "pointer",
        alignItems: "center",
        background: "#f9f9f9",
        borderRadius: "8px",
        padding: "10px 12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        gap: "10px",
      }}
    >
     <div
  style={{
    fontSize: "1.2rem",
    color: "#4caf50",
    transition: "transform 0.2s ease, color 0.2s ease",
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = "scale(1.25)";
    e.currentTarget.style.color = "#2e7d32";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = "scale(1)";
    e.currentTarget.style.color = "#4caf50";
  }}
>
  {icon}
</div>
      <div>
        <div style={{ fontWeight: "bold", color: "#333" }}>{label}</div>
        <div style={{ color: "#2e7d32" }}>
          {isText ? value : displayValue}
        </div>
      </div>
    </div>
  );
};

const buttonStyle = (bg) => ({
  padding: "16px",
  backgroundColor: bg,
  color: "#fff",
  fontWeight: "bold",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  width: "100%",
  fontSize: "1.1rem",
  marginTop: "10px",
});

export default SwapDetails;
